import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { clasesAlumnosAPI, alumnosAPI } from '../../services/api';
import { formatTime, getDayName, copyToClipboard } from '../../services/utils';
import { 
  PaperAirplaneIcon, 
  ClipboardDocumentIcon, 
  UserGroupIcon,
  ClockIcon,
  AcademicCapIcon,
  MusicalNoteIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ClassDetailModal = ({ isOpen, onClose, classData }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && classData) {
      fetchClassStudents();
    }
  }, [isOpen, classData]);

  const fetchClassStudents = async () => {
    try {
      setLoading(true);
      const studentsRequest = classData?.Curso
        ? alumnosAPI.getByYear(classData.Curso)
        : alumnosAPI.getAll();

      const [assignmentsRes, studentsRes] = await Promise.all([
        clasesAlumnosAPI.getByClaseId(classData.IDClase),
        studentsRequest
      ]);

      const studentIds = new Set(assignmentsRes.data.map(item => Number(item.AlumnoID)));
      
      if (studentIds.size > 0) {
        const classStudents = studentsRes.data.filter(student => 
          !student.F_Baja_ME && studentIds.has(Number(student.IDAlumno))
        );
        setStudents(classStudents);
      } else {
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching class students:', err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsApp = (student) => {
    if (!student.Telefono1) {
      toast.error('El alumno no tiene teléfono registrado');
      return;
    }
    
    const message = encodeURIComponent(
      `Recordatorio de clase: ${classData.Asignatura} - ${formatTime(classData.H_Inicio)} a ${formatTime(classData.H_Final)}`
    );
    
    window.open(`https://wa.me/34${student.Telefono1}?text=${message}`, '_blank');
  };

  const handleCopyEmails = () => {
    const emails = students
      .map(student => student.Email1)
      .filter(email => email)
      .join('; ');
    
    if (!emails) {
      toast.error('No hay emails disponibles');
      return;
    }
    
    copyToClipboard(
      emails,
      () => toast.success('Emails copiados al portapapeles'),
      (error) => toast.error(error || 'Error al copiar los emails')
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalle de Clase" size="lg">
      <div className="space-y-6">
        {/* Información de la clase */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-500" />
                Información General
              </h4>
              <dl className="space-y-2">
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Asignatura:</dt>
                  <dd className="text-sm text-gray-900 font-medium">{classData.Asignatura}</dd>
                </div>
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Instrumento:</dt>
                  <dd className="text-sm text-gray-900 flex items-center">
                    <MusicalNoteIcon className="h-4 w-4 mr-1" />
                    {classData.Instrumento || 'No especificado'}
                  </dd>
                </div>
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Profesor:</dt>
                  <dd className="text-sm text-gray-900 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    {classData.Profesor}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
                Horario
              </h4>
              <dl className="space-y-2">
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Día:</dt>
                  <dd className="text-sm text-gray-900">{getDayName(classData.DiaSemana)}</dd>
                </div>
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Hora:</dt>
                  <dd className="text-sm text-gray-900 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {formatTime(classData.H_Inicio)} - {formatTime(classData.H_Final)}
                  </dd>
                </div>
                <div className="flex items-start">
                  <dt className="w-32 text-sm text-gray-500">Curso:</dt>
                  <dd className="text-sm text-gray-900">{classData.Curso}/{classData.Curso + 1}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Lista de alumnos */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-gray-900 flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2" />
              Alumnos ({students.length})
            </h4>
            <div className="flex space-x-2">
              <button
                onClick={handleCopyEmails}
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={students.length === 0}
              >
                <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                Copiar Emails
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : students.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {students.map(student => (
                <div
                  key={student.IDAlumno}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {student.Nombre} {student.Apellido1} {student.Apellido2}
                    </div>
                    <div className="text-sm text-gray-600">
                      {student.Email1 && <span>{student.Email1}</span>}
                      {student.Email1 && student.Telefono1 && <span> • </span>}
                      {student.Telefono1 && <span>{student.Telefono1}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSendWhatsApp(student)}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!student.Telefono1}
                    title={student.Telefono1 ? 'Enviar WhatsApp' : 'Sin teléfono registrado'}
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
              No hay alumnos asignados a esta clase
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ClassDetailModal;
